import {
  AmazonIcon,
  ArchitectureIcon,
  BitcoinIcon,
  BlurbLogo,
  BookIcon,
  BusinessIcon,
  ExitIcon,
  GlobeIcon,
  HistoryIcon,
  MastercardIcon,
  ShiningStarIcon,
  SlackIcon,
  StarBurstIcon,
  VisaIcon,
} from '@/components/react/icons';
import { ICONS, ICON_COLORS, type IconName } from '@/consts';
import { cva, cx, type VariantProps } from '@/cva.config';
import { Icon as IconifyIcon } from '@iconify/react';
import type { ComponentProps } from 'react';

const iconVariants = cva({
  base: 'shrink-0 grow-0',
  variants: {
    size: {
      extraSmall: 'text-xs',
      small: 'text-base',
      large: 'text-3xl',
      extraLarge: 'text-[3rem]',
      custom: null,
    },
    color: {
      ...ICON_COLORS,
    },
  },
});

// Map of custom SVG components
const customIcons = {
  'custom/architecture': ArchitectureIcon,
  'custom/book': BookIcon,
  'custom/blurb-logo': BlurbLogo,
  'custom/business': BusinessIcon,
  'custom/exit': ExitIcon,
  'custom/globe': GlobeIcon,
  'custom/history': HistoryIcon,
  'custom/shining-star': ShiningStarIcon,
  'custom/slack': SlackIcon,
  'custom/star-burst': StarBurstIcon,
  'payment/amazon': AmazonIcon,
  'payment/bitcoin': BitcoinIcon,
  'payment/mastercard': MastercardIcon,
  'payment/visa': VisaIcon,
} as const;

// Type guard to check if iconString is a custom icon key
const isCustomIcon = (
  iconString: string,
): iconString is keyof typeof customIcons => iconString in customIcons;

interface ReactIconProps
  extends VariantProps<typeof iconVariants>,
    Omit<ComponentProps<typeof IconifyIcon>, 'icon' | 'className' | 'color'> {
  name: IconName;
  className?: string | undefined;
}

export const ReactIcon = ({
  className,
  color,
  name,
  size,
  ...props
}: ReactIconProps) => {
  const iconString = ICONS[name];
  const CustomIconComponent =
    isCustomIcon(iconString) ? customIcons[iconString] : null;

  return CustomIconComponent ?
      <CustomIconComponent
        className={cx(iconVariants({ size, color }), className)}
        {...props}
      />
    : <IconifyIcon
        className={cx(iconVariants({ size, color }), className)}
        icon={iconString}
        {...props}
      />;
};
